/**
 * ConfidenceBar — horizontal confidence level indicator.
 *
 * A filled track that communicates AI classification confidence
 * at a glance without requiring the writer to interpret a raw
 * percentage. Three sizes:
 *
 *   sm  2px — inline in DocRow, below the metaRow badges
 *   md  4px — panel header in ClassificationIntelPanel
 *   lg  8px — primary display in ClassificationIntelPanel body
 *
 * Color follows the evidence quality tiers:
 *   ≥ 85%  → evidenceHigh (emerald)
 *   ≥ 60%  → evidenceMid  (amber)
 *   < 60%  → evidenceLow  (red)
 *
 * The track uses role="meter" for screen-reader accessibility.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { fonts, inkColors } from "../tokens";
import { aiColors } from "../tokens";

export interface ConfidenceBarProps {
  /** 0..1 value from source_classification.source_doctype_confidence. */
  confidence: number;
  /** sm = 2px (DocRow), md = 4px, lg = 8px (intel panel). */
  size?: "sm" | "md" | "lg";
  /** Show percentage label after the track. */
  showLabel?: boolean;
}

const HEIGHT: Record<string, string> = {
  sm: "2px",
  md: "4px",
  lg: "8px",
};

export function confColor(c: number): string {
  if (c >= 0.85) return aiColors.evidenceHigh;
  if (c >= 0.6) return aiColors.evidenceMid;
  return aiColors.evidenceLow;
}

const useStyles = makeStyles({
  root: {
    display: "flex",
    alignItems: "center",
    columnGap: "6px",
    width: "100%",
  },
  track: {
    flex: 1,
    borderRadius: "9999px",
    backgroundColor: "rgba(0,0,0,0.07)",
    overflow: "hidden",
    // height applied inline so size prop drives it
  },
  fill: {
    borderRadius: "9999px",
    height: "100%",
    // width + backgroundColor applied inline
  },
  label: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    fontWeight: 700,
    flexShrink: 0,
    letterSpacing: "0.3px",
    minWidth: "26px",
    textAlign: "right",
  },
});

export const ConfidenceBar: React.FC<ConfidenceBarProps> = ({ confidence, size = "sm", showLabel = false }) => {
  const styles = useStyles();
  const clamped = Math.min(1, Math.max(0, confidence));
  const pct = Math.round(clamped * 100);
  const color = confColor(clamped);

  return (
    <div
      className={styles.root}
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`Classification confidence ${pct}%`}
    >
      <div className={styles.track} style={{ height: HEIGHT[size] }}>
        <div
          className={styles.fill}
          style={{
            width: `${pct}%`,
            backgroundColor: color,
          }}
        />
      </div>
      {showLabel && (
        <span className={styles.label} style={{ color }}>
          {pct}%
        </span>
      )}
    </div>
  );
};

export default ConfidenceBar;
